import { useEffect, useRef, useState } from 'react'
import { EXPORT_EXTENSION, fetchExport, type ExportFormat } from '../api'
import { useI18n, type MessageKey } from '../i18n'
import { track } from '../lib/analytics'
import { baseName, saveBlob } from '../lib/download'
import styles from './RowExport.module.css'

const FORMATS: ExportFormat[] = ['txt', 'srt', 'vtt', 'json']

/** The quick "Eksporto" on a finished row: a small menu of formats that downloads the latest saved version. */
export function RowExport({ id, filename, onError }: { id: string; filename: string | null; onError: (message: string) => void }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<ExportFormat | null>(null)
  const box = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !box.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', close)
    }
  }, [open])

  const run = async (format: ExportFormat) => {
    setBusy(format)
    try {
      const blob = await fetchExport(id, format)
      saveBlob(blob, `${baseName(filename)}.${EXPORT_EXTENSION[format]}`)
      track('export', { format })
      setOpen(false)
    } catch {
      onError(t('export.failed'))
    } finally {
      setBusy(null)
    }
  }

  const label = `${t('export.label')}: ${filename ?? id}`
  return (
    <span className={styles.box} ref={box}>
      <button type="button" className="btn btn-small" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen((v) => !v)}>
        {t('export.label')}
      </button>
      {open ? (
        <ul className={styles.menu} role="menu" aria-label={label}>
          {FORMATS.map((format) => (
            <li key={format} role="none">
              <button type="button" role="menuitem" className={styles.item} disabled={busy !== null} onClick={() => void run(format)}>
                {t(`export.${format}` as MessageKey)}
                {busy === format ? ' …' : ''}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  )
}
